import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { AdminShell } from "./Admin";
import { api, formatDetail } from "../lib/api";
import { renderMarkdown, formatPostDate } from "../lib/markdown";
import useMeta from "../hooks/useMeta";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { title: "", excerpt: "", body: "", cover_image_url: "", publish_date: today(), published: false };
const field = { width: "100%", padding: "12px 14px", border: "1px solid rgba(184,150,90,.35)", background: "#fff", fontFamily: "var(--sans)", fontSize: ".95rem", color: "var(--charcoal)", outline: "none" };
const label = { display: "block", fontSize: ".68rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--warm-gray)", marginBottom: 8 };

function OwnerSignIn() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };
  return (
    <div className="form-section" data-testid="admin-blog-signin">
      <form onSubmit={submit} className="form-wrap" style={{ maxWidth: 440 }}>
        <span className="brand-mark" style={{ display: "inline-block", marginBottom: 32 }}>SAVIAR</span>
        <p className="eyebrow eyebrow-light">Blog Admin</p>
        <h1 style={{ fontFamily: "var(--serif)", fontWeight: 300, color: "var(--ivory)", fontSize: "2.2rem", margin: "12px 0 32px" }}>Owner sign in</h1>
        <div className="form-group"><label>Email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-blog-email" /></div>
        <div className="form-group"><label>Password</label><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-blog-password" /></div>
        {error && <p style={{ color: "#e08b8b", fontSize: ".85rem", marginBottom: 16 }} data-testid="admin-blog-signin-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn btn-primary" style={{ width: "100%" }} data-testid="admin-blog-signin-submit">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </div>
  );
}

function Toolbar({ taRef, value, onChange }) {
  const wrap = (before, after = before, placeholder = "text") => {
    const ta = taRef.current; const s = ta.selectionStart; const e = ta.selectionEnd;
    const sel = value.slice(s, e) || placeholder;
    onChange(value.slice(0, s) + before + sel + after + value.slice(e));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + before.length, s + before.length + sel.length); }, 0);
  };
  const line = (prefix) => {
    const ta = taRef.current; const s = ta.selectionStart;
    const start = value.lastIndexOf("\n", s - 1) + 1;
    onChange(value.slice(0, start) + prefix + value.slice(start));
    setTimeout(() => ta.focus(), 0);
  };
  const tools = [
    ["Heading", () => line("## ")], ["Subheading", () => line("### ")],
    ["Bold", () => wrap("**")], ["Italic", () => wrap("*")],
    ["Bullet list", () => line("- ")], ["Numbered list", () => line("1. ")],
    ["Link", () => { const url = window.prompt("Link URL (https://...)"); if (url) wrap("[", `](${url})`, "link text"); }],
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }} data-testid="editor-toolbar">
      {tools.map(([t, fn]) => (
        <button key={t} type="button" onClick={fn} style={{ padding: "6px 12px", border: "1px solid rgba(184,150,90,.4)", background: "transparent", fontSize: ".75rem", cursor: "pointer", color: "var(--charcoal)" }} data-testid={`tool-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</button>
      ))}
    </div>
  );
}

function PostForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const taRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { title: f.title, excerpt: f.excerpt, body: f.body, cover_image_url: f.cover_image_url, publish_date: f.publish_date, published: f.published };
    try {
      if (f.id) await api.patch(`/admin/blog/${f.id}`, payload); else await api.post("/admin/blog", payload);
      toast.success("Post saved."); onSaved();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="portal-card" style={{ display: "grid", gap: 20 }} data-testid="post-form">
      <h3 style={{ margin: 0 }}>{f.id ? "Edit post" : "New post"}</h3>
      <div><label style={label}>Title</label><input style={field} required value={f.title} onChange={set("title")} data-testid="post-title" /></div>
      <div><label style={label}>Excerpt (one or two sentences)</label><textarea style={{ ...field, minHeight: 70 }} value={f.excerpt} onChange={set("excerpt")} data-testid="post-excerpt" /></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        <div><label style={label}>Cover image URL (optional)</label><input style={field} value={f.cover_image_url} onChange={set("cover_image_url")} placeholder="https://..." data-testid="post-cover" /></div>
        <div><label style={label}>Publish date</label><input type="date" style={field} required value={f.publish_date} onChange={set("publish_date")} data-testid="post-date" /></div>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={label}>Body</label>
          <button type="button" className="btn-ghost" onClick={() => setPreview(!preview)} data-testid="post-preview-toggle">{preview ? "Edit" : "Preview"}</button>
        </div>
        {preview ? (
          <div style={{ ...field, minHeight: 320 }} data-testid="post-preview">{renderMarkdown(f.body)}</div>
        ) : (
          <>
            <Toolbar taRef={taRef} value={f.body} onChange={(body) => setF((p) => ({ ...p, body }))} />
            <textarea ref={taRef} style={{ ...field, minHeight: 320, lineHeight: 1.7 }} value={f.body} onChange={set("body")} data-testid="post-body" />
            <p style={{ fontSize: ".75rem", color: "var(--warm-gray)", marginTop: 6 }}>Leave a blank line between paragraphs. Use the toolbar for headings, bold, italic, lists, and links.</p>
          </>
        )}
      </div>
      <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }} data-testid="post-published">
        <input type="checkbox" checked={f.published} onChange={set("published")} style={{ accentColor: "var(--gold)" }} />
        <span style={{ fontSize: ".9rem" }}>Published (visible on the public blog)</span>
      </label>
      <div style={{ display: "flex", gap: 12 }}>
        <button type="submit" disabled={busy} className="btn btn-primary" data-testid="post-save">{busy ? "Saving…" : "Save post"}</button>
        <button type="button" onClick={onCancel} className="btn btn-outline-dark" data-testid="post-cancel">Cancel</button>
      </div>
    </form>
  );
}

function PostRow({ p, onEdit, onToggle, onDelete }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "18px 0", borderBottom: "1px solid rgba(184,150,90,.15)", flexWrap: "wrap" }} data-testid={`admin-post-${p.id}`}>
      <div>
        <p style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", margin: "0 0 4px" }}>{p.title}</p>
        <p style={{ fontSize: ".78rem", color: "var(--warm-gray)", margin: 0 }}>{formatPostDate(p.publish_date)} · /blog/{p.slug} · <strong style={{ color: p.published ? "#3D7A4A" : "var(--warm-gray)" }}>{p.published ? "Published" : "Draft"}</strong></p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onEdit} className="btn btn-outline-dark" style={{ padding: "8px 14px" }} data-testid={`edit-${p.id}`}>Edit</button>
        <button onClick={onToggle} className="btn btn-outline-dark" style={{ padding: "8px 14px" }} data-testid={`toggle-${p.id}`}>{p.published ? "Unpublish" : "Publish"}</button>
        <button onClick={onDelete} className="btn btn-outline-dark" style={{ padding: "8px 14px", color: "#8B2A2A", borderColor: "#8B2A2A" }} data-testid={`delete-${p.id}`}>Delete</button>
      </div>
    </div>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => toast.error("Could not load posts"));
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try { await api.patch(`/admin/blog/${p.id}`, { ...p, published: !p.published }); load(); }
    catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blog/${p.id}`); toast.success("Post deleted."); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <AdminShell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div><p className="eyebrow" style={{ marginBottom: 8 }}>Admin</p><h1 className="display" style={{ fontSize: "2rem", color: "var(--charcoal)", margin: 0 }}>Blog</h1></div>
        {!editing && <button onClick={() => setEditing(EMPTY)} className="btn btn-primary" data-testid="new-post">New post</button>}
      </div>
      {editing ? (
        <PostForm key={editing.id || "new"} initial={{ ...EMPTY, ...editing }} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <div className="portal-card" data-testid="admin-post-list">
          {posts.length === 0 ? <p className="body-sm" style={{ margin: 0 }}>No posts yet. Create your first post.</p> :
            posts.map((p) => <PostRow key={p.id} p={p} onEdit={() => setEditing(p)} onToggle={() => toggle(p)} onDelete={() => remove(p)} />)}
        </div>
      )}
    </AdminShell>
  );
}

export default function AdminBlog() {
  useMeta("Blog Admin | Saviar");
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user || user.role !== "admin") return <OwnerSignIn />;
  return <BlogManager />;
}
